/**
 * The Vorgang: created once by either entry path, then written to as a log
 * of versions (design §9, §4.1). `demo.ts` stays the transport for the
 * outline solver and the planner; this is the transport for the case itself
 * — the two meet where a run's answer becomes a version (`postVersion`'s
 * `document`, filled from `DemoRunResult.documentJson`).
 */
import { type QueryClient, useQuery } from "@tanstack/react-query";

import { ProblemError, toProblemDetails, type components } from "@k60/contracts";

import { CUTOUT_CLEARANCE_DEFAULT_MM } from "@/lib/placement";

import { api } from "./client";
import type { DemoRunResult } from "./demo";
import { RECORD_KEY, RECORD_NOT_FOUND, type CaseRecordOut } from "./register";

export type CaseOut = components["schemas"]["CaseOut"];
export type CaseDetailOut = components["schemas"]["CaseDetailOut"];
export type DocumentOut = components["schemas"]["DocumentOut"];
/** The body `POST .../versions` takes — derived from the generated schema
 * rather than retyped by hand, so a field the backend adds or renames shows
 * up here as a type error instead of silently going unsent. */
export type VersionInput = components["schemas"]["VersionIn"];
/** The Stand's form for „Form ändern …“ (spec 2026-10-15 §3) — every figure with its source. */
export type StandSeedOut = components["schemas"]["StandSeedOut"];
/** The edited form on its way back as a Basisstand. */
export type BasisIn = components["schemas"]["BasisIn"];
export type VersionOut = components["schemas"]["VersionOut"];

/**
 * What "nothing has been planned yet" looks like in the wire shape
 * (`PlanningStateIn`'s own defaults). Both entry paths write version 1 right
 * after the outline exists and before anybody has placed a single element —
 * `planningState` is required on every version (`plan_version.planning_state
 * NOT NULL`, design §4.1), so this is what they send.
 */
export const EMPTY_STATE: components["schemas"]["PlanningStateIn"] = {
  placements: [],
  consoleSeries: "solid_1",
  consoleAdjustableFirst: false,
  consoleOverrides: {},
  cutoutClearanceMm: CUTOUT_CLEARANCE_DEFAULT_MM,
  cutoutRatePerMNet: null,
  cutoutBaseNet: null,
  cutoutSurcharges: {},
  splitPriority: "standard_first",
};

/**
 * Thrown by `postVersion` on a 409: somebody else's gesture landed first.
 * Carries the version number the server is actually at, so a caller (Task
 * 15) can reload from there instead of just failing.
 */
export class StaleVersionError extends Error {
  readonly name = "StaleVersionError";

  constructor(readonly latestVersionNo: number) {
    super(`Stand ${latestVersionNo} ist inzwischen der letzte.`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * The 409 body is `VersionConflictOut` (`{ latestVersionNo }`), not a
 * problem+json shape — read directly from either of the two places it can
 * arrive: `error` on the stubbed `{ data, error, response }` a test hands
 * back, or `ProblemError.problem.raw` where the client's shared error
 * middleware already turned it into a thrown error (`problem.ts`'s `raw`
 * exists for exactly this).
 *
 * Returns rather than throws (Fix round 1, Minor 3): a malformed 409 body —
 * conflict without a number, which should never happen but is not this
 * client's to assume — becomes a `ProblemError` with a German detail rather
 * than a bare `Error`, so `postVersion` keeps its two-shape invariant
 * (`StaleVersionError` or `ProblemError`, never anything else) whichever
 * branch calls this.
 *
 * `ConflictOut.detail` is the server's own sentence about *this* refusal —
 * "Der Vorgang ist versendet — Änderungen sind nicht mehr möglich." on a
 * frozen Vorgang, for one (S-8). Used whenever the body actually carries a
 * non-empty one; the fixed fallback stays for the shape that carries
 * neither a number nor a sentence, which should not happen but is exactly
 * the case the comment above already keeps a fallback for.
 */
function staleVersionFrom(payload: unknown): StaleVersionError | ProblemError {
  const latestVersionNo = isRecord(payload) ? payload.latestVersionNo : undefined;
  if (typeof latestVersionNo === "number") {
    return new StaleVersionError(latestVersionNo);
  }
  const rawDetail = isRecord(payload) ? payload.detail : undefined;
  const detail =
    typeof rawDetail === "string" && rawDetail !== ""
      ? rawDetail
      : "Der Server nennt keinen aktuellen Stand zu diesem Konflikt.";
  return new ProblemError(
    {
      type: "about:blank",
      title: "Konflikt",
      status: 409,
      detail,
      raw: payload,
    },
    null,
  );
}

/** `POST /api/v1/cases` — the first call on both entry paths (design §9). */
export async function createCase(input: {
  title: string;
  sourceChannel: "upload" | "manual";
  gratingType: string | null;
}): Promise<CaseOut> {
  const { data, response } = await api.POST("/api/v1/cases", { body: input });
  if (!response.ok || !data) throw new Error("Vorgang konnte nicht angelegt werden.");
  return data;
}

/** `POST /api/v1/cases/{id}/documents` — multipart, repeated `files`. */
export async function uploadDocuments(caseId: string, files: File[]): Promise<DocumentOut[]> {
  const body = new FormData();
  for (const file of files) body.append("files", file, file.name);
  const { data, response } = await api.POST("/api/v1/cases/{case_id}/documents", {
    params: { path: { case_id: caseId } },
    // openapi-fetch's generated body type expects the JSON shape this
    // operation's schema declares; the actual wire content type is
    // multipart/form-data and the real body is this `FormData` — the cast
    // is the seam between the two, not a way around either.
    body: body as never,
  });
  if (!response.ok || !data) throw new Error("Hochladen fehlgeschlagen.");
  return data;
}

/** `POST /api/v1/cases/from-mail`: the Vorgang, the mail's entry in its file, the drawings that came with it. */
export type CaseFromMailOut = components["schemas"]["CaseFromMailOut"];

/**
 * Thrown by `createCaseFromMail` on a 409: this mail already lies in one of my
 * own Vorgänge. Carries that Vorgang, so the screen can offer it instead of a
 * second one (spec 2026-10-20 §3.1).
 */
export class MailAlreadyFiledError extends Error {
  readonly name = "MailAlreadyFiledError";

  constructor(
    readonly caseId: string,
    readonly caseNo: string,
    detail: string = "Diese Mail liegt schon in einem Vorgang.",
  ) {
    super(detail);
  }
}

/**
 * The 409 body is `MailAlreadyFiledOut` (`{ detail, caseId, caseNo, entryId }`)
 * — read from wherever it arrived: `error` on the stubbed reply a test hands
 * back, or `ProblemError.problem.raw` where the shared error middleware
 * already turned it into a thrown error. A 409 without a Vorgang in it is not
 * this one's and stays whatever it was.
 */
function mailAlreadyFiled(payload: unknown): MailAlreadyFiledError | undefined {
  if (!isRecord(payload)) return undefined;
  const { caseId, caseNo, detail } = payload;
  if (typeof caseId !== "string" || typeof caseNo !== "string") return undefined;
  return typeof detail === "string" && detail !== ""
    ? new MailAlreadyFiledError(caseId, caseNo, detail)
    : new MailAlreadyFiledError(caseId, caseNo);
}

/**
 * `POST /api/v1/cases/from-mail` — multipart, repeated `files`: exactly one
 * mail (`.eml`/`.msg`), and the drawings that may come with it. One
 * transaction on the server: either the Vorgang with the mail and its
 * drawings exists afterwards, or nothing does — so a failed call is simply
 * sent again.
 *
 * A mail that already lies in one of my Vorgänge is `MailAlreadyFiledError`;
 * every other refusal is the `ProblemError` the screen shows the sentence of
 * (`.userMessage`), whichever of the two shapes it arrived in.
 */
export async function createCaseFromMail(files: File[]): Promise<CaseFromMailOut> {
  const body = new FormData();
  for (const file of files) body.append("files", file, file.name);
  let reply;
  try {
    reply = await api.POST("/api/v1/cases/from-mail", {
      // The same seam as `uploadDocuments`: JSON-shaped generated type,
      // multipart wire, this cast is where the two meet.
      body: body as never,
    });
  } catch (cause) {
    if (cause instanceof ProblemError && cause.status === 409) {
      throw mailAlreadyFiled(cause.problem.raw) ?? cause;
    }
    throw cause;
  }
  const { data, error, response } = reply;
  if (response.status === 409) {
    const filed = mailAlreadyFiled(error);
    if (filed) throw filed;
  }
  if (!response.ok || !data) {
    throw new ProblemError(toProblemDetails(error, response.status), null);
  }
  return data;
}

/**
 * The bare network call, isolated in its own try/catch so its one failure
 * mode — the real client's shared error middleware (`api/client.ts`)
 * throwing `ProblemError` for a 409 before `postVersion` below ever reads a
 * `response.status` — is handled exactly once, here, and never reprocessed
 * by the stubbed-shape branch that runs after this returns. A test that
 * stubs `api.POST` directly bypasses this catch entirely and hands the 409
 * back as an ordinary `{ data, error, response }` instead.
 */
async function postVersionRequest(caseId: string, input: VersionInput) {
  try {
    return await api.POST("/api/v1/cases/{case_id}/versions", {
      params: { path: { case_id: caseId } },
      body: input,
    });
  } catch (cause) {
    if (cause instanceof ProblemError && cause.status === 409) {
      throw staleVersionFrom(cause.problem.raw);
    }
    throw cause;
  }
}

/**
 * `POST /api/v1/cases/{id}/versions` — one gesture on its way into the log.
 *
 * Two shapes reach the 409 case: the stubbed `{ data, error, response }` a
 * test hands back without throwing (`response.status === 409` below), and
 * the real client's thrown `ProblemError` (`postVersionRequest` above).
 * Both resolve to the same `StaleVersionError` through `staleVersionFrom`;
 * every other non-2xx becomes a `ProblemError` — either the one the
 * middleware already threw, or, in the stubbed shape, one built here from
 * the same `toProblemDetails` the middleware itself uses (`auth.ts`'s
 * `fetchMe`/`login` follow the identical two-shape pattern).
 */
export async function postVersion(
  caseId: string,
  input: VersionInput,
): Promise<{ versionNo: number }> {
  const { data, error, response } = await postVersionRequest(caseId, input);
  if (response.status === 409) throw staleVersionFrom(error);
  if (!response.ok || !data) {
    throw new ProblemError(toProblemDetails(error, response.status), null);
  }
  return data;
}

/**
 * Everything cached about one Vorgang — its facts and documents here, the
 * record and the plan underneath (`RECORD_KEY`, `["case", id, "plan"]`).
 * Invalidating it refreshes all of them at once.
 */
export const CASE_KEY = (caseId: string | null) => ["case", caseId] as const;

/**
 * A Stand was just written: what the cache holds of the Vorgang says so
 * before the screen moves on — its detail and its record carry at least
 * `versionNo` (ruling R-6). The frame of the page it moves to reads them at
 * once; with the Stand still at 0 it drew the tabs of a Vorgang without one
 * — „Auslese" or „Umriss" beside „Planung" — and swapped them with the next
 * answer.
 *
 * Only what the cache holds is raised, never lowered, and nothing is made up
 * for an answer it does not hold. The answers are still read again: the
 * caller invalidates `CASE_KEY` after this, as before.
 */
export function noteStandWritten(client: QueryClient, caseId: string, versionNo: number): void {
  client.setQueryData<CaseDetailOut>(CASE_KEY(caseId), (old) =>
    old && old.latestVersionNo < versionNo ? { ...old, latestVersionNo: versionNo } : old,
  );
  client.setQueryData<CaseRecordOut>(RECORD_KEY(caseId), (old) =>
    old && old.latestVersionNo < versionNo ? { ...old, latestVersionNo: versionNo } : old,
  );
}

/**
 * `GET /api/v1/cases/{id}` — the Vorgang's own facts, plus its documents.
 *
 * A 404 — unknown, foreign, or handed over a moment ago; the owner check is
 * the only one on this route — becomes `RECORD_NOT_FOUND`, in both shapes a
 * reply arrives in, like the record's (final review of Plan D, I-1): the
 * frame and the board tell a Vorgang that is gone from one that failed to
 * load by that sentence, also while the cache still holds its last answer.
 */
export function useCase(caseId: string | null) {
  return useQuery({
    queryKey: CASE_KEY(caseId),
    enabled: caseId !== null,
    queryFn: () => fetchCase(caseId!),
  });
}

/** `GET /api/v1/cases/{id}` — `useCase`'s own read, for a prefetch too. */
export async function fetchCase(caseId: string): Promise<CaseDetailOut> {
  try {
    const { data, response } = await api.GET("/api/v1/cases/{case_id}", {
      params: { path: { case_id: caseId } },
    });
    if (response.status === 404) throw new Error(RECORD_NOT_FOUND);
    if (!response.ok || !data) throw new Error("Vorgang konnte nicht geladen werden.");
    return data;
  } catch (cause) {
    if (cause instanceof ProblemError && cause.status === 404) throw new Error(RECORD_NOT_FOUND);
    throw cause;
  }
}

/**
 * The Vorgang read ahead, into `useCase`'s entry — for a question that will
 * want it in a moment (review final, 3: „Angebot raus" on hover or focus,
 * so Enter, Enter does not outrun the mail's list). Not again within
 * 30 seconds of the last read; a failure stays in the entry for the
 * question to say.
 */
export function prefetchCase(client: QueryClient, caseId: string): void {
  void client.prefetchQuery({ queryKey: CASE_KEY(caseId), queryFn: () => fetchCase(caseId), staleTime: 30_000 });
}

export type CaseStatus = CaseOut["status"];

export const CASES_KEY = ["cases"] as const;

/** The caller's own cases, newest change first — exactly what the API sorts. */
export function useCases() {
  return useQuery({
    queryKey: CASES_KEY,
    queryFn: async (): Promise<CaseOut[]> => {
      const { data, response } = await api.GET("/api/v1/cases");
      if (!data) throw new ProblemError(toProblemDetails(null, response.status), null);
      return data;
    },
    staleTime: 30_000,
  });
}

/**
 * One of the stages a person sets by hand (design 2026-09-07 §5.2) — and
 * „Vorgang stornieren …" / „Wiederherstellen" (spec 2026-10-15 §5), which
 * say why: `reason` is **mandatory** for `cancelled` (the server answers 422
 * without it) and optional when a cancelled Vorgang is restored. Without a
 * reason the key is left out, not sent as `null`.
 *
 * A refused move comes back as the client's thrown `ProblemError` (409),
 * carrying the server's reason — the caller shows its own sentence and reloads.
 */
export async function setCaseStatus(caseId: string, to: CaseStatus, reason?: string): Promise<CaseOut> {
  const { data, response } = await api.POST("/api/v1/cases/{case_id}/status", {
    params: { path: { case_id: caseId } },
    body: reason === undefined ? { to } : { to, reason },
  });
  if (!data) throw new ProblemError(toProblemDetails(null, response.status), null);
  return data;
}

/** `pressrost` · `barfuss` · `laengsstab` — the words `PUT …/grating-type` takes. */
export type GratingTypeWord = components["schemas"]["GratingTypeIn"]["gratingType"];

/**
 * What `PUT …/grating-type` answers: the Vorgang, and the Stand it stands at
 * afterwards (`latestVersionNo`, Ruling G-3) — one more where the change
 * wrote a Stand, what it was otherwise.
 */
export type GratingTypeOut = components["schemas"]["GratingTypeOut"];

/**
 * `PUT /api/v1/cases/{id}/grating-type` — the Vorgang's grating type (spec
 * 2026-10-23 R-5). The same type is 200 and writes nothing; on a Vorgang
 * with a Stand a change writes a new Stand (R-1), so its calculation goes
 * stale, a release ends and the Vorgang is „In Planung“ again.
 *
 * The answer names the Stand the Vorgang stands at afterwards — the number a
 * board's next gesture posts against (Ruling G-3).
 *
 * Refusals as `ProblemError`, like `setCaseStatus`: 409 (`case_cancelled`,
 * an offer that is out — the server's own sentence), 422 (a Stand without a
 * measure: „kein Stand ohne Maß“, the type stays as it was), 404.
 */
export async function setGratingType(caseId: string, gratingType: GratingTypeWord): Promise<GratingTypeOut> {
  const { data, error, response } = await api.PUT("/api/v1/cases/{case_id}/grating-type", {
    params: { path: { case_id: caseId } },
    body: { gratingType },
  });
  if (!data) throw new ProblemError(toProblemDetails(error, response.status), null);
  return data;
}

/**
 * The type is the Vorgang's now: the cached Vorgang says so at once — its
 * type, the status the change may have taken back to planning, and the
 * Stand it wrote (`noteStandWritten`, Ruling G-3) — and everything under
 * `CASE_KEY` (the plan above all, which a new Stand moved, the record, the
 * calculation) and every list that shows the Vorgang (`CASES_KEY`) is read
 * again. Resolves once those reads are back.
 */
export async function gratingTypeSaved(client: QueryClient, caseId: string, saved: GratingTypeOut): Promise<void> {
  client.setQueryData<CaseDetailOut>(CASE_KEY(caseId), (old) =>
    old
      ? { ...old, gratingType: saved.gratingType, status: saved.status, statusChangedAt: saved.statusChangedAt }
      : old,
  );
  noteStandWritten(client, caseId, saved.latestVersionNo);
  await Promise.all([
    client.invalidateQueries({ queryKey: CASE_KEY(caseId) }),
    client.invalidateQueries({ queryKey: CASES_KEY }),
  ]);
}

/**
 * The sentence `CaseRoute` matches on to tell "this Vorgang has no Stand yet"
 * apart from "the server could not be reached". A constant, not two string
 * literals in two files: the screen used to compare against its own copy, and
 * a comparison against a copy is a comparison that drifts.
 */
export const NO_PLAN_YET = "Dieser Vorgang hat noch keinen Stand.";

/**
 * Which of the plan route's two 404s this is (final review of Plan D, I-1):
 * the owner check's `CASE_NOT_FOUND` — the Vorgang is not mine (any more),
 * handed over while its board was open — or the route's own „no Stand yet".
 * Told apart by the owner check's sentence, which is `RECORD_NOT_FOUND`
 * word for word; every other 404 here is the missing Stand.
 */
function planNotFound(detail: string | undefined): string {
  return detail === RECORD_NOT_FOUND ? RECORD_NOT_FOUND : NO_PLAN_YET;
}

/**
 * `GET /api/v1/cases/{id}/plan` — replayed from the log, laid out, in the
 * one shape every planning screen already reads (`DemoRunResult`).
 *
 * The 404 is read off the thrown `ProblemError`, not off `response.status`:
 * the shared client's error middleware throws on every non-2xx, so the
 * `response.status` line below never ran for a real 404 and the screen showed
 * the generic server detail instead of its own sentence. Same shape as
 * `postVersion`'s 409 above — the status check stays for the mocked answer a
 * test hands back without throwing. Either shape becomes `NO_PLAN_YET` or,
 * for a Vorgang that is gone, `RECORD_NOT_FOUND` (`planNotFound`).
 *
 * `fresh` asks the server again on every mount, cached answer or not: a
 * board's saves move the Stand without touching this cache, so a screen that
 * judges from the board (the Eingang's type question, Ruling G-2) may not
 * trust a cached answer — it reads `isFetchedAfterMount` before it does.
 */
export function useCasePlan(caseId: string | null, { fresh = false }: { fresh?: boolean } = {}) {
  return useQuery({
    queryKey: ["case", caseId, "plan"],
    enabled: caseId !== null,
    refetchOnMount: fresh ? "always" : true,
    queryFn: async (): Promise<DemoRunResult> => {
      try {
        const { data, error, response } = await api.GET("/api/v1/cases/{case_id}/plan", {
          params: { path: { case_id: caseId! } },
        });
        if (response.status === 404) throw new Error(planNotFound(toProblemDetails(error, 404).detail));
        if (!response.ok || !data) throw new Error("Plan konnte nicht geladen werden.");
        return data as unknown as DemoRunResult;
      } catch (cause) {
        if (cause instanceof ProblemError && cause.status === 404) {
          throw new Error(planNotFound(cause.problem.detail));
        }
        throw cause;
      }
    },
  });
}

/**
 * `GET /api/v1/cases/{caseId}/documents/{documentId}/pages/{n}` — a stored
 * drawing's page, for an `<img>` `src` rather than a fetch this client makes
 * itself (Task 10). `DemoRunResult.document.id` is the *case* id once a run
 * belongs to a Vorgang (`useCasePlan`'s own answer, design §9) — `demoPageUrl`
 * would ask the demo process for a document it has never heard of, so a
 * screen showing a stored case's drawing needs this instead, built from
 * `useCase(caseId).documents[0].id`.
 *
 * Same base every other call in `api/client.ts` uses — empty by default
 * (same origin), `VITE_API_BASE_URL` where the two are split.
 */
export function casePageUrl(caseId: string, documentId: string, page: number): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? "";
  return `${base}/api/v1/cases/${encodeURIComponent(caseId)}/documents/${encodeURIComponent(documentId)}/pages/${page}`;
}

/**
 * The same page drawn small (at most 800 px on its longest edge) — for
 * choosing which page a reading reads (Seitenwahl, 13.10.2026), never what
 * the model sees.
 */
export function casePagePreviewUrl(caseId: string, documentId: string, page: number): string {
  return `${casePageUrl(caseId, documentId, page)}?preview=true`;
}

/**
 * The Stand's form as the outline editor begins from it („Form ändern …“,
 * spec 2026-10-15 §3). Under `CASES_KEY` like the brief has it: whatever
 * invalidates the register's lists marks this stale too.
 */
export function standSeedKey(caseId: string) {
  return ["cases", caseId, "stand-seed"] as const;
}

/**
 * `GET /api/v1/cases/{id}/outline/stand-seed` — the latest Stand's form and
 * figures, each with its source. Reads only. A refusal comes back as
 * `ProblemError` whose `problem.raw` is the server's `ConflictOut` —
 * `reasons: ["no_stand"]` for a Vorgang without one — in both answer shapes
 * (the middleware's throw, a test's stubbed `{ data, error, response }`).
 */
export async function standSeed(caseId: string): Promise<StandSeedOut> {
  const { data, error, response } = await api.GET("/api/v1/cases/{case_id}/outline/stand-seed", {
    params: { path: { case_id: caseId } },
  });
  if (!data) throw new ProblemError(toProblemDetails(error, response.status), null);
  return data;
}

/**
 * `POST /api/v1/cases/{id}/outline/basis` — the edited form becomes a
 * Basisstand: a new Stand with its own document, the board laid on the old
 * form gone, the Vorgang's settings kept (spec §3). Refusals as
 * `ProblemError`, like `setCaseStatus`: 409 `ConflictOut` (`case_cancelled`
 * · a sent Vorgang · a stale Stand with `latestVersionNo` · `no_stand`), 422
 * with the server's sentence where the form or a figure does not fit.
 */
export async function postBasis(caseId: string, body: BasisIn): Promise<VersionOut> {
  const { data, error, response } = await api.POST("/api/v1/cases/{case_id}/outline/basis", {
    params: { path: { case_id: caseId } },
    body,
  });
  if (!data) throw new ProblemError(toProblemDetails(error, response.status), null);
  return data;
}
